import { useEffect, useRef, useState } from "react";
import {
  Camera,
  ImageUp,
  Lock,
  Mail,
  RotateCcw,
  Save,
  Trash2,
  User,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ProfileAvatar } from "@/shared/ui/ProfileAvatar";
import {
  PROFILE_PHOTO_ACCEPT,
  removeProfilePhoto,
  saveProfilePhoto,
  useProfilePhoto,
} from "@/shared/lib/profilePhoto";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass } from "../components/FormField";
import { getAccountAvatarInitials } from "../branding";
import {
  getAuthToken,
  getAuthUser,
  getStoredAccountType,
  storeAuth,
} from "../auth";
import {
  meRequest,
  updateAuthPasswordRequest,
  updateAuthProfileRequest,
} from "../api/companyApi";
import { cn } from "@/shared/lib/cn";

type ProfileTab = "information" | "security";

export type UserProfilePageConfig = {
  homeHref: string;
  badge: string;
  companySectionTitle?: string;
  companyNameLabel?: string;
  showOrgFields?: boolean;
  initialUsername: string;
  initialEmail: string;
};

type UserProfilePageProps = {
  config: UserProfilePageConfig;
};

export function UserProfilePage({ config }: UserProfilePageProps) {
  const { t } = useTranslation();
  const labels = t.safecheck.settingsPage.userProfile;
  const { showToast } = usePageActions();
  const profilePhoto = useProfilePhoto();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handlePhotoFile = async (file: File | undefined) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      await saveProfilePhoto(file);
      showToast("Logo de profil enregistré.");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Logo non enregistré.");
    } finally {
      setPhotoBusy(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handleRemovePhoto = () => {
    removeProfilePhoto();
    showToast("Logo de profil retiré.");
  };
  const {
    homeHref,
    badge,
    companySectionTitle,
    companyNameLabel,
    showOrgFields = false,
    initialUsername,
    initialEmail,
  } = config;

  const accountType = getStoredAccountType();
  const [activeTab, setActiveTab] = useState<ProfileTab>("information");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [username, setUsername] = useState(initialUsername);
  const [email, setEmail] = useState(initialEmail);
  const [orgName, setOrgName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [security, setSecurity] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;

    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const { user } = await meRequest();
        if (cancelled) return;
        setUsername(user.name);
        setEmail(user.email);
        const existing = getAuthUser();
        if (existing) {
          storeAuth(token, { ...existing, ...user });
        }
      } catch {
        // garde les valeurs session locales
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleReset = () => {
    const user = getAuthUser();
    setUsername(user?.name ?? initialUsername);
    setEmail(user?.email ?? initialEmail);
    setSecurity({ currentPassword: "", newPassword: "", confirmPassword: "" });
  };

  const handleSaveProfile = async () => {
    if (!getAuthToken()) {
      showToast("Connexion requise.");
      return;
    }
    setSaving(true);
    try {
      const { user, message } = await updateAuthProfileRequest({ username, email });
      const token = getAuthToken();
      if (token) {
        storeAuth(token, user);
      }
      setUsername(user.name);
      setEmail(user.email);
      showToast(message ?? t.actions.saved);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePassword = async () => {
    if (!getAuthToken()) {
      showToast("Connexion requise.");
      return;
    }
    if (security.newPassword !== security.confirmPassword) {
      showToast("Les mots de passe ne correspondent pas.");
      return;
    }
    if (security.newPassword.length < 6) {
      showToast("Le nouveau mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    setSaving(true);
    try {
      const { message } = await updateAuthPasswordRequest({
        current_password: security.currentPassword,
        password: security.newPassword,
        password_confirmation: security.confirmPassword,
      });
      setSecurity({ currentPassword: "", newPassword: "", confirmPassword: "" });
      showToast(message ?? "Mot de passe mis à jour.");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Mise à jour du mot de passe impossible.");
    } finally {
      setSaving(false);
    }
  };

  const displayName = username || email || "SC";

  return (
    <>
      <Breadcrumbs homeHref={homeHref} items={[{ label: labels.pageTitle }]} />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[300px_1fr]">
        <aside className="theme-card p-5">
          <div className="flex flex-col items-center text-center">
            <input
              ref={photoInputRef}
              type="file"
              accept={PROFILE_PHOTO_ACCEPT}
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              onChange={(event) => void handlePhotoFile(event.target.files?.[0])}
            />
            <div className="relative">
              <ProfileAvatar
                initials={getAccountAvatarInitials(accountType, displayName)}
                src={profilePhoto}
                size={88}
              />
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={photoBusy}
                aria-label={profilePhoto ? "Changer le logo de profil" : "Téléverser un logo de profil"}
                title={profilePhoto ? "Changer le logo" : "Téléverser un logo"}
                className="absolute -bottom-1 -right-1 grid h-9 w-9 place-items-center rounded-full border-2 border-card bg-accent text-accent-foreground shadow-[var(--shadow-sm)] transition-transform duration-150 hover:scale-105 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:opacity-60"
              >
                <Camera size={16} strokeWidth={2} aria-hidden="true" />
              </button>
            </div>
            <p className="mt-4 text-lg font-semibold text-foreground">{displayName}</p>
            <p className="mt-1 text-xs leading-relaxed text-muted">{email}</p>
            <span className="mt-3 inline-flex rounded-full bg-accent-soft px-3 py-1 text-[11px] font-bold tracking-wide text-accent-foreground">
              {badge}
            </span>
          </div>

          <div className="mt-6 border-t border-border pt-5">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">
              {labels.contactTitle}
            </p>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-2.5">
                <Mail size={15} className="mt-0.5 shrink-0 text-primary" />
                <span className="break-all">{email || "—"}</span>
              </div>
              <div className="flex items-start gap-2.5">
                <User size={15} className="mt-0.5 shrink-0 text-primary" />
                <span>{username || "—"}</span>
              </div>
            </div>
          </div>
        </aside>

        <div className="theme-card overflow-hidden">
          <div className="border-b border-border px-5 pt-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setActiveTab("information")}
                className={cn(
                  "inline-flex items-center gap-2 rounded-t-lg px-4 py-2.5 text-sm font-medium transition-colors",
                  activeTab === "information"
                    ? "bg-primary text-white"
                    : "text-muted-foreground hover:bg-accent/20",
                )}
              >
                <User size={15} />
                {labels.tabs.information}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("security")}
                className={cn(
                  "inline-flex items-center gap-2 rounded-t-lg px-4 py-2.5 text-sm font-medium transition-colors",
                  activeTab === "security"
                    ? "bg-primary text-white"
                    : "text-muted-foreground hover:bg-accent/20",
                )}
              >
                <Lock size={15} />
                {labels.tabs.security}
              </button>
            </div>
          </div>

          <div className="p-5 md:p-6">
            {loading ? (
              <p className="text-sm text-muted">Chargement du profil…</p>
            ) : activeTab === "information" ? (
              <div className="space-y-8">
                <section>
                  <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted">
                    {labels.sections.userAccount}
                  </h2>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField label={labels.fields.username}>
                      <input
                        value={username}
                        onChange={(event) => setUsername(event.target.value)}
                        className={inputClass}
                      />
                    </FormField>
                    <FormField label={labels.fields.email}>
                      <input
                        type="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        className={inputClass}
                      />
                    </FormField>
                  </div>
                </section>

                {showOrgFields && (
                  <section>
                    <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted">
                      {companySectionTitle ?? labels.sections.companyInfo}
                    </h2>
                    <div className="space-y-4">
                      <FormField label={companyNameLabel ?? labels.fields.companyName}>
                        <input
                          value={orgName}
                          onChange={(event) => setOrgName(event.target.value)}
                          className={inputClass}
                          disabled
                          title="Géré depuis le profil organisation"
                        />
                      </FormField>
                      <FormField label={labels.fields.address}>
                        <input
                          value={address}
                          onChange={(event) => setAddress(event.target.value)}
                          className={inputClass}
                          disabled
                        />
                      </FormField>
                      <div className="md:w-1/2">
                        <FormField label={labels.fields.phone}>
                          <input
                            value={phone}
                            onChange={(event) => setPhone(event.target.value)}
                            className={inputClass}
                            disabled
                          />
                        </FormField>
                      </div>
                      <p className="text-xs text-muted">
                        Les infos organisation se modifient dans « Profil entreprise / prestataire ».
                      </p>
                    </div>
                  </section>
                )}

                <section>
                  <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted">
                    {labels.sections.profilePhoto}
                  </h2>
                  <div
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(event) => {
                      event.preventDefault();
                      setDragOver(false);
                      void handlePhotoFile(event.dataTransfer.files?.[0]);
                    }}
                    className={cn(
                      "flex flex-col items-center gap-4 rounded-xl border-2 border-dashed p-5 text-center transition-colors duration-150 sm:flex-row sm:text-left",
                      dragOver ? "border-accent bg-accent/10" : "border-border bg-card-muted/40",
                    )}
                  >
                    <ProfileAvatar
                      initials={getAccountAvatarInitials(accountType, displayName)}
                      src={profilePhoto}
                      size={64}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">
                        {profilePhoto ? "Logo de profil enregistré" : "Ajoutez le logo de votre organisation"}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Glissez une image ici ou choisissez un fichier. PNG, JPG, WebP ou SVG, 2 Mo maximum.
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => photoInputRef.current?.click()}
                        disabled={photoBusy}
                        className="inline-flex items-center gap-2 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
                      >
                        <ImageUp size={15} aria-hidden="true" />
                        {photoBusy ? "Traitement…" : profilePhoto ? "Changer" : "Téléverser"}
                      </button>
                      {profilePhoto ? (
                        <button
                          type="button"
                          onClick={handleRemovePhoto}
                          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 size={15} aria-hidden="true" />
                          Retirer
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-muted">
                    Le logo est conservé dans ce navigateur et s'affiche dans l'en-tête.
                  </p>
                </section>
              </div>
            ) : (
              <section>
                <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted">
                  {labels.sections.security}
                </h2>
                <div className="grid max-w-2xl grid-cols-1 gap-4">
                  <FormField label={labels.fields.currentPassword}>
                    <input
                      type="password"
                      value={security.currentPassword}
                      onChange={(event) =>
                        setSecurity((current) => ({
                          ...current,
                          currentPassword: event.target.value,
                        }))
                      }
                      className={inputClass}
                      autoComplete="current-password"
                    />
                  </FormField>
                  <FormField label={labels.fields.newPassword}>
                    <input
                      type="password"
                      value={security.newPassword}
                      onChange={(event) =>
                        setSecurity((current) => ({
                          ...current,
                          newPassword: event.target.value,
                        }))
                      }
                      className={inputClass}
                      autoComplete="new-password"
                    />
                  </FormField>
                  <FormField label={labels.fields.confirmPassword}>
                    <input
                      type="password"
                      value={security.confirmPassword}
                      onChange={(event) =>
                        setSecurity((current) => ({
                          ...current,
                          confirmPassword: event.target.value,
                        }))
                      }
                      className={inputClass}
                      autoComplete="new-password"
                    />
                  </FormField>
                </div>
              </section>
            )}

            <div className="mt-8 flex flex-wrap gap-3 border-t border-border pt-5">
              <button
                type="button"
                disabled={saving || loading}
                onClick={() =>
                  void (activeTab === "security" ? handleSavePassword() : handleSaveProfile())
                }
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
              >
                <Save size={16} />
                {activeTab === "security" ? labels.updatePassword : labels.save}
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleReset}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:opacity-60"
              >
                <RotateCcw size={16} />
                {labels.reset}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
